import { Check, Minus } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import api from "../api/client";
import { trackProductEvent } from "../analytics";
import { configValue } from "../config";

export type PublicPlan = {
  code: string;
  name: string;
  package: "free" | "team" | "business";
  monthly_price: string;
  yearly_price: string | null;
  currency: string;
  seat_limit: number | null;
  trial_days: number;
  entitlements: string[];
  contact_sales: boolean;
  price_includes_vat: boolean;
  vat_rate: string;
  trial_mandate_amount: string;
};

const fallbackPlans: PublicPlan[] = [
  {
    code: "free",
    name: "Free",
    package: "free",
    monthly_price: "0",
    yearly_price: null,
    currency: "EUR",
    seat_limit: 1,
    trial_days: 0,
    entitlements: [],
    contact_sales: false,
    price_includes_vat: true,
    vat_rate: "21.00",
    trial_mandate_amount: "0.01",
  },
  {
    code: "team",
    name: "Team",
    package: "team",
    monthly_price: "",
    yearly_price: null,
    currency: "EUR",
    seat_limit: null,
    trial_days: 0,
    entitlements: [],
    contact_sales: false,
    price_includes_vat: true,
    vat_rate: "21.00",
    trial_mandate_amount: "0.01",
  },
  {
    code: "business",
    name: "Business",
    package: "business",
    monthly_price: "0",
    yearly_price: null,
    currency: "EUR",
    seat_limit: null,
    trial_days: 0,
    entitlements: [],
    contact_sales: true,
    price_includes_vat: true,
    vat_rate: "21.00",
    trial_mandate_amount: "0.01",
  },
];

const planCopy = {
  free: {
    description: "Voor je eigen verlofplanning.",
    features: [
      "1 gebruiker",
      "Jaarplanner en saldi",
      "Schoolvakanties en feestdagen",
      "PWA en eigen export",
    ],
  },
  team: {
    description: "Voor kleine werkgevers en teams.",
    features: [
      "Medewerkers uitnodigen",
      "Aanvragen en fiatteren",
      "Teamplanning en bezetting",
      "Rapportages en exports",
    ],
  },
  business: {
    description: "Voor organisaties met meer structuur.",
    features: [
      "Meerdere teams en managers",
      "Afdelingen en locaties",
      "Uitgebreide policies",
      "Audit en ruimere limieten",
    ],
  },
};

function priceLabel(plan: PublicPlan) {
  if (plan.package === "free")
    return (
      <>
        <strong>€ 0</strong>
        <span>voor persoonlijk gebruik</span>
      </>
    );
  if (plan.contact_sales)
    return (
      <>
        <strong>Op maat</strong>
        <span>afgestemd op je organisatie</span>
      </>
    );
  if (!plan.monthly_price)
    return (
      <>
        <strong>Configureerbaar</strong>
        <span>per maand</span>
      </>
    );
  return (
    <>
      <strong>
        {new Intl.NumberFormat("nl-NL", {
          style: "currency",
          currency: plan.currency,
        }).format(Number(plan.monthly_price))}
      </strong>
      <span>per maand, inclusief 21% btw</span>
    </>
  );
}

export default function PricingTable({ compact = false }: { compact?: boolean }) {
  const [plans, setPlans] = useState<PublicPlan[]>(fallbackPlans);
  useEffect(() => {
    void api
      .get<PublicPlan[]>("/billing/plans/")
      .then((response) => {
        if (response.data.length) setPlans(response.data);
      })
      .catch(() => undefined);
  }, []);
  const contactEmail = configValue("CONTACT_EMAIL") || "info@deverlofkaart.nl";

  return (
    <div className={`pricing-grid${compact ? " compact" : ""}`}>
      {plans.map((plan) => {
        const copy = planCopy[plan.package];
        const target = plan.contact_sales
          ? `mailto:${contactEmail}?subject=Business`
          : `/registreren?plan=${plan.code}`;
        const buttonClass = `button ${
          plan.package === "team" ? "primary" : "subtle"
        } full-width`;
        const buttonLabel = plan.contact_sales
          ? "Neem contact op"
          : plan.package === "free"
          ? "Gratis beginnen"
          : "Kies dit pakket";
        return (
          <article
            key={plan.code}
            className={`pricing-card${plan.package === "team" ? " featured" : ""}`}
          >
            {plan.package === "team" && (
              <span className="plan-ribbon">Meest gekozen</span>
            )}
            <div>
              <p className="eyebrow">{plan.name}</p>
              <h2>{copy.description}</h2>
            </div>
            <p className="price-line">{priceLabel(plan)}</p>
            {plan.contact_sales ? (
              <a
                className={buttonClass}
                href={target}
                onClick={() => trackProductEvent("pricing_cta")}
              >
                {buttonLabel}
              </a>
            ) : (
              <Link
                className={buttonClass}
                to={target}
                onClick={() => trackProductEvent("pricing_cta")}
              >
                {buttonLabel}
              </Link>
            )}
            <ul>
              {copy.features.map((feature) => (
                <li key={feature}>
                  <Check size={16} /> {feature}
                </li>
              ))}
            </ul>
            {!compact && (
              <p className="plan-limit">
                <Minus size={14} />{" "}
                {plan.seat_limit
                  ? `Maximaal ${plan.seat_limit} seat${
                      plan.seat_limit === 1 ? "" : "s"
                    }`
                  : "Limieten vanuit pakketconfiguratie"}
              </p>
            )}
          </article>
        );
      })}
    </div>
  );
}
